import React, { useEffect, useMemo, useState } from 'react';
import { useCoordination } from '@atlassiansox/engagekit-ts';
import { calendarReleasesModalEntrypoint } from '@atlassian/jira-calendar-releases-multi-projects-modal/entrypoint.tsx';
import { CoordinationClient } from '@atlassian/jira-engagement/src/ui/coordination-client/index.tsx';
import { ModalEntryPointContainer } from '@atlassian/jira-entry-point-modal-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import type { Environment } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useCalendarMoreMenuStore } from '../../../controllers/calendar-more-menu-store/index.tsx';

export const MODAL_ENGAGEMENT_ID_STG = 'jwm-show-multi_jsw_proj-releases-in-cal';
export const MODAL_ENGAGEMENT_ID_PROD = 'jwm_cal_show_multi_jsw_proj_releases';
const ENV_STAGING: Environment = 'staging';

// see jira/src/packages/business/list/src/ui/list-view/base-table/fields-dropdown/dropdown/trigger-button/index.tsx
// and here https://hello.atlassian.net/wiki/spaces/JSMOKIT/pages/1022344142/External+Messages+in+Jira#Advanced%3A-Using-the-CoordinationClient-object
const STARGATE_URL = '/gateway/api';
/**
 *  This component integrates with the Engagement Platform through CoordinationClient class to
 *  determine if modal should be displayed. If it should, then it asynchronously loads the modal entrypoint.
 */
export function CalendarReleaseMultiProjectOnboardingModal() {
	const MODAL_ENGAGEMENT_ID =
		useEnvironment() === ENV_STAGING ? MODAL_ENGAGEMENT_ID_STG : MODAL_ENGAGEMENT_ID_PROD;
	/**
	 * Onboarding Experience is loaded manually on page load via EP's CoordinationClient class
	 * Once loaded, if the message is active for this particular context, only then the modal will be loaded and displayed
	 */
	const { cloudId } = useTenantContext();
	const coordinationClient = useMemo(
		() => new CoordinationClient(cloudId, STARGATE_URL),
		[cloudId],
	);
	const [isEnrolled, stopEnrolment] = useCoordination(coordinationClient, MODAL_ENGAGEMENT_ID);
	const { entryPointReferenceSubject, entryPointActions } = useEntryPoint(
		calendarReleasesModalEntrypoint,
		{},
	);

	const [, moreMenuActions] = useCalendarMoreMenuStore();
	const ctaAction = () => {
		stopEnrolment();
		moreMenuActions.open('jsw-multi-proj-release-modal');
	};

	// Prevents loading modal entry point multiple times
	// The entryPointActions dependency changes a couple of times when the Relay entry points are loaded and unloaded.
	// This causes useEffect to trigger too many times, including causing a modal reload when it's supposed to unload
	const [modalLoaded, setModalLoaded] = useState(false);
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (isEnrolled && !modalLoaded) {
			setModalLoaded(true);
			entryPointActions.load();
		}
	}, [isEnrolled, entryPointActions, setModalLoaded, modalLoaded]);

	return (
		<ModalEntryPointContainer
			entryPointReferenceSubject={entryPointReferenceSubject}
			entryPointActions={entryPointActions}
			id="calendar-releases-multi-projects-modal"
			packageName="calendar-releases-multi-projects-modal"
			teamName="jira-werewolves"
			runtimeProps={{
				onActionClick: ctaAction,
				onDismissClick: stopEnrolment,
			}}
		/>
	);
}
